import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  IconButton,
  Text,
  Collapse,
  Portal,
  useDisclosure,
  Flex,
  Avatar,
  Icon,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import {
  FiMenu,
  FiX,
  FiLogOut,
  FiShield,
  FiUserPlus,
  FiChevronRight,
  FiDollarSign,
  FiUsers,
  FiClipboard,
  FiCalendar,
  FiDatabase,
  FiSettings,
  FiGrid,
  FiActivity,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoginWithSmsModal } from '../pages/authentication';
import { getColor } from '../brandConfig';
import { ModuleNavItem, ModuleConfig } from '../types/ModuleConfig';
import { useDraggableFloat, computeMenuPlacement } from '../hooks/useDraggableFloat';
import { useHideToEdge } from '../hooks/useHideToEdge';
import { LAUNCHER, LauncherEdgeTab, LauncherHideButton } from './floating/LauncherChrome';

// Import module configs (used as fallback when modules prop not provided)
import lmModuleConfig from '../pages/lm/moduleConfig';
import governanceModuleConfig from '../pages/governance/moduleConfig';
import dashboardModuleConfig from '../pages/dashboard/moduleConfig';
import clientsModuleConfig from '../pages/clients/moduleConfig';
import siteEditorModuleConfig from '../pages/site-editor/moduleConfig';
import assessmentSnapshotModuleConfig from '../pages/assessment-snapshot/moduleConfig';
import billsModuleConfig from '../pages/bills/moduleConfig';
import emailsModuleConfig from '../pages/emails/moduleConfig';
import leadflowModuleConfig from '../pages/leadflow/moduleConfig';
import { calendarsModuleConfig } from '../pages/calendars/moduleConfig';

/** The site's modules — ONE list, shared with AdminModuleLinks (home-page quick links for admins). */
export const allModuleConfigs = [governanceModuleConfig, dashboardModuleConfig, assessmentSnapshotModuleConfig, billsModuleConfig, lmModuleConfig, clientsModuleConfig, siteEditorModuleConfig, emailsModuleConfig, leadflowModuleConfig, calendarsModuleConfig];

/**
 * Home of the main floating nav: the MIDDLE of the LEFT edge, on phone and desktop (Tom,
 * 2026-10-24). The module menu (ModuleNavLauncher, moduleNavHome) also lives at middle-left,
 * so this sits one button-height + 12px ABOVE it — the two never overlap.
 */
export const navbarHome = (_vw: number, vh: number) => ({
  left: 16,
  top: Math.max(64, Math.round(vh / 2 - 28 - 56 - 12)),
});

/** Module id → react-icons component, for modules whose config icon is an Fi* name. */
export const moduleIcons: Record<string, any> = {};

interface FloatingNavbarProps {
  modules?: ModuleConfig[]; // Resolved modules from App.tsx with backend permissions
}

// Animations
const slideIn = keyframes`
  from { transform: translateX(-100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
  100% { transform: translateY(0px); }
`;

// Admin links - require ADMIN or TENANT_MASTER_ADMIN
const adminLinks = [
  { label: 'Calendars', path: '/calendars', icon: FiCalendar },
  { label: 'Company Knowledge', path: '/companyknowledge', icon: FiDatabase },
  { label: 'SEO Metadata', path: '/seometadata', icon: FiSettings },
];

const BUTTON_SIZE = 56;
const EDGE_PAD = 8;

export const FloatingNavbar: React.FC<FloatingNavbarProps> = ({ modules: resolvedModules }) => {
  const { isAuthenticated, user, logout, refreshAuth } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const { isOpen: isLoginOpen, onOpen: onLoginOpen, onClose: onLoginClose } = useDisclosure();
  const navigate = useNavigate();

  // Draggable launcher position (persists across sessions, per-device).
  // See vault/how-to/draggable-floating-launchers.md for the full pattern.
  // NOTE 2026-06-16: the key was previously `..._${user?.id || 'guest'}`, but
  // `user` is null at mount (auth still loading), so useDraggableFloat's one-time
  // useState initializer read the EMPTY `_guest` key → always reset to the
  // top-right default; drags then saved under `_<realId>`, a key never read back.
  // A stable per-device key is read correctly at mount AND matches the real usage
  // (each person is on their own device), so the launcher now restores where it
  // was left.
  const { position, setPosition, dragHandlers, wasJustDragged } = useDraggableFloat({
    storageKey: `surgready_floating_navbar_pos`,
    size: BUTTON_SIZE,
    edgePad: EDGE_PAD,
    defaultPosition: navbarHome, // middle-left (Tom, 2026-10-24), stacked just above the module menu
  });

  // Hide-to-edge (2026-10-25, Tom: the main nav was the one launcher without it). Same pattern as
  // ModuleNavLauncher and master's FloatingNavbar: hidden = ONLY the edge tab renders; the footer's
  // reset un-hides. Per-device key, like the position above (registered prefix in floatingPanels.ts).
  const { hidden, setHidden, justHidden } = useHideToEdge('surgready_floating_navbar_hidden');

  // The footer's "↻ Reset floating panels" (2026-10-24 — it moved every launcher except this one):
  // snap home AND clear our own key, so the reset also survives a reload.
  useEffect(() => {
    const onReset = () => {
      try { localStorage.removeItem('surgready_floating_navbar_pos'); } catch { /* noop */ }
      setIsExpanded(false);
      setPosition(navbarHome(window.innerWidth, window.innerHeight));
    };
    window.addEventListener('tms:reset-floating-panels', onReset);
    return () => window.removeEventListener('tms:reset-floating-panels', onReset);
  }, [setPosition]);

  // Viewport dimensions tracked in state so computeMenuPlacement recomputes on rotate/resize.
  const [viewport, setViewport] = useState(() =>
    typeof window !== 'undefined'
      ? { w: window.innerWidth, h: window.innerHeight }
      : { w: 1200, h: 800 }
  );
  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const panelPlacement = computeMenuPlacement(
    { left: position.left, top: position.top, size: BUTTON_SIZE },
    viewport,
    { preferredWidth: 320, estimatedHeight: 480, gap: 12 },
    EDGE_PAD,
  );

  const userPerms: string[] = user?.permissions || [];
  const isAdmin = userPerms.includes('ADMIN') || userPerms.includes('TENANT_MASTER_ADMIN');

  // Use resolved modules from App.tsx (with backend permissions), fallback to static configs
  const moduleConfigs = React.useMemo(() => {
    if (!resolvedModules) return allModuleConfigs;
    // Merge resolved permissions into known configs
    const resolvedMap = new Map(resolvedModules.map((m) => [m.id, m]));
    return allModuleConfigs.map((mod) => {
      const resolved = resolvedMap.get(mod.id);
      return resolved || mod;
    });
  }, [resolvedModules]);

  // Helper to find a module config by ID
  const getModuleConfig = (moduleId: string): ModuleConfig | undefined =>
    moduleConfigs.find((m) => m.id === moduleId);

  /**
   * Check if user has any of the permissions for a module's view level.
   * ADMIN bypass is handled by the backend — every module's permissions.ts
   * includes ADMIN in its permission arrays, so ADMIN users always match.
   */
  const hasModuleAccess = (moduleId: string): boolean => {
    if (isAdmin) return true;
    const mod = getModuleConfig(moduleId);
    if (!mod?.permissions?.view || mod.permissions.view.length === 0) return false;
    return userPerms.some((p) => mod.permissions.view.includes(p));
  };

  /**
   * Filter navigation items based on user's permissions.
   * Checks both `permissions` (hardcoded) and `permissionLevel` (resolved from module.permissions).
   */
  const filterNavItems = (moduleId: string, navigation?: ModuleNavItem[]): ModuleNavItem[] => {
    if (!navigation) return [];
    if (isAdmin) return navigation;
    const mod = getModuleConfig(moduleId);
    return navigation.filter((navItem) => {
      // If has explicit permissions array, check directly
      if (navItem.permissions && navItem.permissions.length > 0) {
        return userPerms.some((p) => navItem.permissions!.includes(p));
      }
      // If has permissionLevel, resolve via the module's resolved permissions
      if (navItem.permissionLevel && mod?.permissions) {
        const levelPerms: string[] = (mod.permissions as any)[navItem.permissionLevel] || [];
        if (levelPerms.length === 0) return false;
        return userPerms.some((p) => levelPerms.includes(p));
      }
      // No restriction — show to all authenticated users
      return true;
    });
  };

  // Refresh auth data when component mounts
  useEffect(() => {
    if (isAuthenticated && (!user || !user.permissions)) {
      refreshAuth();
    }
  }, [isAuthenticated, user, refreshAuth]);

  const handleNavItemClick = (path: string) => {
    navigate(path);
    setIsExpanded(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsExpanded(false);
  };

  // Close menu when clicking outside. With three Portals (menu + main FAB + mini-logout FAB)
  // we must exempt all three containers so clicks inside any of them don't close the menu.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        isExpanded &&
        !target.closest('.floating-navbar-container') &&
        !target.closest('.floating-navbar-menu') &&
        !target.closest('.floating-navbar-logout')
      ) {
        setIsExpanded(false);
        setActiveSection(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isExpanded]);

  // Only render the floating nav when authenticated. Once authenticated,
  // render EVEN IF `user` is null/loading so the logout escape hatch stays
  // reachable (fixes the lockout where broken auth hid the only way out).
  if (!isAuthenticated) return null;

  // Hidden: the edge tab, and nothing else — it is the guarantee that hiding can't strand anyone.
  // The tab sits at this launcher's HOME height, not its dragged one: both launchers hug the left
  // edge, so a tab at a dragged-near-the-module-menu height lands under the module menu's own hide
  // button and can't be pressed (harness 2026-10-25). Home is one button above the module menu.
  if (hidden) {
    return (
      <Portal>
        <LauncherEdgeTab
          edge="left"
          top={navbarHome(viewport.w, viewport.h).top}
          bg={getColor("primary")}
          zIndex={LAUNCHER.Z_FAB}
          label="Show the navigation button again"
          announce={justHidden}
          onClick={() => setHidden(false)}
        />
      </Portal>
    );
  }

  return (
    <>
      {/* ===== Portal A — Menu panel (viewport-safe placement, separate from FAB) ===== */}
      <Portal>
        <Box
          className="floating-navbar-menu"
          position="fixed"
          {...(panelPlacement.top !== undefined ? { top: `${panelPlacement.top}px` } : {})}
          {...(panelPlacement.bottom !== undefined ? { bottom: `${panelPlacement.bottom}px` } : {})}
          left={`${panelPlacement.left}px`}
          width={`${panelPlacement.width}px`}
          zIndex={999}
          pointerEvents={isExpanded ? 'auto' : 'none'}
        >
          <Collapse in={isExpanded} animateOpacity>
            <Box
              bg="rgba(255, 255, 255, 0.98)"
              backdropFilter="blur(20px)"
              borderRadius="2xl"
              border="1px solid"
              borderColor={getColor("border.light")}
              p={4}
              maxH="70vh"
              overflowY="auto"
              boxShadow="0 10px 40px rgba(0, 0, 0, 0.15)"
              animation={`${slideIn} 0.3s ease-out`}
              css={{
                '&::-webkit-scrollbar': {
                  width: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: getColor("secondary"),
                  borderRadius: '2px',
                },
              }}
            >
              {/* User Profile Section — ALWAYS rendered when menu opens, even if user
                  is null (profile never loaded). Logout stays reachable in all states. */}
              <Flex
                align="center"
                justify="space-between"
                mb={4}
                p={3}
                bg="rgba(74, 144, 226, 0.05)"
                borderRadius="lg"
                border="1px solid"
                borderColor="rgba(74, 144, 226, 0.1)"
              >
                <HStack
                  spacing={3}
                  cursor={user ? 'pointer' : 'default'}
                  onClick={user ? () => handleNavItemClick('/profile') : undefined}
                  _hover={user ? { opacity: 0.8 } : undefined}
                  transition="opacity 0.2s"
                >
                  {user ? (
                    <>
                      <Avatar
                        size="sm"
                        name={`${user.fName || user.firstName || ""} ${user.lName || user.lastName || ""}`}
                        src={user.profilePhoto || undefined}
                        bg={getColor("secondary")}
                      />
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm" fontWeight="600" color="gray.700">
                          {user.fName || user.firstName || 'User'} {user.lName || user.lastName || ''}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {user.email || user.phoneNumber}
                        </Text>
                      </VStack>
                    </>
                  ) : (
                    <VStack align="start" spacing={0}>
                      <Text fontSize="sm" fontWeight="600" color="gray.700">
                        Signed in
                      </Text>
                      <Text fontSize="xs" color="orange.500">
                        Profile not loaded — try logout
                      </Text>
                    </VStack>
                  )}
                </HStack>
                <IconButton
                  aria-label="Logout"
                  icon={<FiLogOut />}
                  size="sm"
                  variant="ghost"
                  color="red.400"
                  onClick={handleLogout}
                  _hover={{
                    bg: 'rgba(255, 0, 0, 0.1)',
                    transform: 'scale(1.1)',
                  }}
                />
              </Flex>

              {/* Account links — Security for everyone, Onboarding for admins.
                  NOTE 2026-06-15: these Buttons had no explicit `color`, so the
                  ghost variant rendered near-white text on the light drawer —
                  invisible. Pinned to gray.700 (matching the user card + section
                  headers). Onboarding gate widened to include ADMIN so tenant
                  admins (not just the ONBOARDING/MASTER_ADMIN perm) can reach it. */}
              {isAuthenticated && user && (
                <VStack align="stretch" spacing={1} mb={4}>
                  <Button size="sm" variant="ghost" color="gray.700" justifyContent="flex-start" leftIcon={<FiShield />} onClick={() => handleNavItemClick('/security')}>
                    Security &amp; sign-in
                  </Button>
                  {(user.permissions?.includes('ONBOARDING') || user.permissions?.includes('MASTER_ADMIN') || user.permissions?.includes('ADMIN')) && (
                    <Button size="sm" variant="ghost" color="gray.700" justifyContent="flex-start" leftIcon={<FiUserPlus />} onClick={() => handleNavItemClick('/onboarding')}>
                      Onboard people
                    </Button>
                  )}
                </VStack>
              )}

              {/* Navigation Sections */}
              <VStack spacing={2} align="stretch">
                {/* Medical Assessment Section - permission-filtered via backend */}
                {hasModuleAccess('assessment-snapshot') && (
                  <Box>
                    <Button
                      size="sm"
                      variant="ghost"
                      color="gray.600"
                      fontSize="xs"
                      fontWeight="600"
                      textTransform="uppercase"
                      letterSpacing="wider"
                      justifyContent="space-between"
                      w="full"
                      onClick={() => setActiveSection(activeSection === 'assessment' ? null : 'assessment')}
                      _hover={{
                        bg: 'rgba(74, 144, 226, 0.1)',
                        color: getColor("primary"),
                      }}
                      rightIcon={
                        <Box
                          transform={activeSection === 'assessment' ? 'rotate(90deg)' : 'rotate(0)'}
                          transition="transform 0.2s"
                        >
                          <FiChevronRight />
                        </Box>
                      }
                    >
                      {assessmentSnapshotModuleConfig.icon} Medical Assessment
                    </Button>

                    <Collapse in={activeSection === 'assessment'} animateOpacity>
                      <VStack spacing={1} align="stretch" mt={2} pl={2}>
                        {filterNavItems('assessment-snapshot', assessmentSnapshotModuleConfig.navigation)
                          .map((navItem) => (
                            <Button
                              key={navItem.path}
                              size="sm"
                              variant="ghost"
                              color="gray.700"
                              justifyContent="flex-start"
                              leftIcon={navItem.icon ? <Text fontSize="sm">{navItem.icon}</Text> : <Icon as={FiActivity} />}
                              onClick={() => handleNavItemClick(navItem.path)}
                              _hover={{
                                bg: 'rgba(74, 144, 226, 0.1)',
                                color: getColor("primary"),
                                transform: 'translateX(4px)',
                              }}
                              transition="all 0.2s"
                            >
                              {navItem.label}
                            </Button>
                          ))}
                      </VStack>
                    </Collapse>
                  </Box>
                )}

                {/* Bills Section - permission-filtered via backend */}
                {hasModuleAccess('bills') && (
                  <Box>
                    <Button
                      size="sm"
                      variant="ghost"
                      color="gray.600"
                      fontSize="xs"
                      fontWeight="600"
                      textTransform="uppercase"
                      letterSpacing="wider"
                      justifyContent="space-between"
                      w="full"
                      onClick={() => setActiveSection(activeSection === 'bills' ? null : 'bills')}
                      _hover={{
                        bg: 'rgba(74, 144, 226, 0.1)',
                        color: getColor("primary"),
                      }}
                      rightIcon={
                        <Box
                          transform={activeSection === 'bills' ? 'rotate(90deg)' : 'rotate(0)'}
                          transition="transform 0.2s"
                        >
                          <FiChevronRight />
                        </Box>
                      }
                    >
                      {billsModuleConfig.icon} Billing
                    </Button>

                    <Collapse in={activeSection === 'bills'} animateOpacity>
                      <VStack spacing={1} align="stretch" mt={2} pl={2}>
                        {filterNavItems('bills', billsModuleConfig.navigation)
                          .map((navItem) => (
                            <Button
                              key={navItem.path}
                              size="sm"
                              variant="ghost"
                              color="gray.700"
                              justifyContent="flex-start"
                              leftIcon={navItem.icon ? <Text fontSize="sm">{navItem.icon}</Text> : <Icon as={FiDollarSign} />}
                              onClick={() => handleNavItemClick(navItem.path)}
                              _hover={{
                                bg: 'rgba(74, 144, 226, 0.1)',
                                color: getColor("primary"),
                                transform: 'translateX(4px)',
                              }}
                              transition="all 0.2s"
                            >
                              {navItem.label}
                            </Button>
                          ))}
                      </VStack>
                    </Collapse>
                  </Box>
                )}

                {/* Admin Section - Only show for users with admin permissions */}
                {user?.permissions?.some((p: string) => ['ADMIN', 'TENANT_MASTER_ADMIN'].includes(p)) && (
                  <Box>
                    <Button
                      size="sm"
                      variant="ghost"
                      color="gray.600"
                      fontSize="xs"
                      fontWeight="600"
                      textTransform="uppercase"
                      letterSpacing="wider"
                      justifyContent="space-between"
                      w="full"
                      onClick={() => setActiveSection(activeSection === 'admin' ? null : 'admin')}
                      _hover={{
                        bg: 'rgba(74, 144, 226, 0.1)',
                        color: getColor("primary"),
                      }}
                      rightIcon={
                        <Box
                          transform={activeSection === 'admin' ? 'rotate(90deg)' : 'rotate(0)'}
                          transition="transform 0.2s"
                        >
                          <FiChevronRight />
                        </Box>
                      }
                    >
                      Admin
                    </Button>

                    <Collapse in={activeSection === 'admin'} animateOpacity>
                      <VStack spacing={1} align="stretch" mt={2} pl={2}>
                        {adminLinks.map((link) => (
                          <Button
                            key={link.path}
                            size="sm"
                            variant="ghost"
                            color="gray.700"
                            justifyContent="flex-start"
                            leftIcon={<Icon as={link.icon} />}
                            onClick={() => handleNavItemClick(link.path)}
                            _hover={{
                              bg: 'rgba(74, 144, 226, 0.1)',
                              color: getColor("primary"),
                              transform: 'translateX(4px)',
                            }}
                            transition="all 0.2s"
                          >
                            {link.label}
                          </Button>
                        ))}
                      </VStack>
                    </Collapse>
                  </Box>
                )}

                {/* Learning Management Section - permission-filtered via backend */}
                {hasModuleAccess('lm') && (
                  <Box>
                    <Button
                      size="sm"
                      variant="ghost"
                      color="gray.600"
                      fontSize="xs"
                      fontWeight="600"
                      textTransform="uppercase"
                      letterSpacing="wider"
                      justifyContent="space-between"
                      w="full"
                      onClick={() => setActiveSection(activeSection === 'learning' ? null : 'learning')}
                      _hover={{
                        bg: 'rgba(74, 144, 226, 0.1)',
                        color: getColor("primary"),
                      }}
                      rightIcon={
                        <Box
                          transform={activeSection === 'learning' ? 'rotate(90deg)' : 'rotate(0)'}
                          transition="transform 0.2s"
                        >
                          <FiChevronRight />
                        </Box>
                      }
                    >
                      {lmModuleConfig.icon} Learning
                    </Button>

                    <Collapse in={activeSection === 'learning'} animateOpacity>
                      <VStack spacing={1} align="stretch" mt={2} pl={2}>
                        {filterNavItems('lm', lmModuleConfig.navigation)
                          .map((navItem) => (
                            <Button
                              key={navItem.path}
                              size="sm"
                              variant="ghost"
                              color="gray.700"
                              justifyContent="flex-start"
                              leftIcon={navItem.icon ? <Text fontSize="sm">{navItem.icon}</Text> : <Icon as={FiGrid} />}
                              onClick={() => handleNavItemClick(navItem.path)}
                              _hover={{
                                bg: 'rgba(74, 144, 226, 0.1)',
                                color: getColor("primary"),
                                transform: 'translateX(4px)',
                              }}
                              transition="all 0.2s"
                            >
                              {navItem.label}
                            </Button>
                          ))}
                      </VStack>
                    </Collapse>
                  </Box>
                )}

                {/* Client Management Section - permission-filtered via backend */}
                {hasModuleAccess('clients') && (
                  <Box>
                    <Button
                      size="sm"
                      variant="ghost"
                      color="gray.600"
                      fontSize="xs"
                      fontWeight="600"
                      textTransform="uppercase"
                      letterSpacing="wider"
                      justifyContent="space-between"
                      w="full"
                      onClick={() => setActiveSection(activeSection === 'clients' ? null : 'clients')}
                      _hover={{
                        bg: 'rgba(74, 144, 226, 0.1)',
                        color: getColor("primary"),
                      }}
                      rightIcon={
                        <Box
                          transform={activeSection === 'clients' ? 'rotate(90deg)' : 'rotate(0)'}
                          transition="transform 0.2s"
                        >
                          <FiChevronRight />
                        </Box>
                      }
                    >
                      {clientsModuleConfig.icon} Clients
                    </Button>

                    <Collapse in={activeSection === 'clients'} animateOpacity>
                      <VStack spacing={1} align="stretch" mt={2} pl={2}>
                        {filterNavItems('clients', clientsModuleConfig.navigation)
                          .map((navItem) => (
                            <Button
                              key={navItem.path}
                              size="sm"
                              variant="ghost"
                              color="gray.700"
                              justifyContent="flex-start"
                              leftIcon={navItem.icon ? <Text fontSize="sm">{navItem.icon}</Text> : <Icon as={FiUsers} />}
                              onClick={() => handleNavItemClick(navItem.path)}
                              _hover={{
                                bg: 'rgba(74, 144, 226, 0.1)',
                                color: getColor("primary"),
                                transform: 'translateX(4px)',
                              }}
                              transition="all 0.2s"
                            >
                              {navItem.label}
                            </Button>
                          ))}
                      </VStack>
                    </Collapse>
                  </Box>
                )}

              </VStack>
            </Box>
          </Collapse>
        </Box>
      </Portal>

      {/* ===== Portal B — Main FAB (draggable, pinned at user-persisted position) ===== */}
      <Portal>
        <Box
          className="floating-navbar-container"
          position="fixed"
          top={`${position.top}px`}
          left={`${position.left}px`}
          zIndex={998}
        >
          <IconButton
            aria-label="Toggle navigation — drag to move, tap to open"
            icon={isExpanded ? <FiX /> : <FiMenu />}
            size="lg"
            borderRadius="full"
            bg={getColor("primary")}
            color="white"
            border="2px solid"
            borderColor="rgba(255, 255, 255, 0.2)"
            boxShadow="0 4px 12px rgba(74, 144, 226, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)"
            {...dragHandlers}
            onClick={() => {
              if (wasJustDragged()) return;
              setIsExpanded(!isExpanded);
            }}
            _hover={{
              bg: getColor("secondary"),
              transform: 'scale(1.1) rotate(180deg)',
              boxShadow: "0 6px 20px rgba(74, 144, 226, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.4)"
            }}
            _active={{
              transform: 'scale(0.95)',
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3), inset 0 2px 4px rgba(0, 0, 0, 0.2)"
            }}
            transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
            animation={!isExpanded ? `${float} 3s ease-in-out infinite` : undefined}
          />
          {/* Hide mini-button — tucks the launcher away to a tab on the left edge; hiding also closes the menu. */}
          <LauncherHideButton
            fabSize={BUTTON_SIZE}
            wasJustDragged={wasJustDragged}
            label="Hide the navigation button — a small tab stays on the left edge to bring it back. The footer's ↻ Reset floating panels button also restores it."
            onClick={() => { setIsExpanded(false); setHidden(true); }}
          />
        </Box>
      </Portal>

      {/* ===== Portal C — Emergency logout mini-FAB (always visible when authenticated) =====
          Escape hatch for broken auth state: if permissions never load, `user` is null,
          or the menu won't open for any reason, tapping this immediately clears the
          session. Sits directly above the main FAB and tracks its dragged position. */}
      <Portal>
        <Box
          className="floating-navbar-logout"
          position="fixed"
          top={`${Math.max(EDGE_PAD, position.top - 40)}px`}
          left={`${position.left + 14}px`}
          zIndex={997}
        >
          <IconButton
            aria-label="Emergency logout (always visible)"
            icon={<FiLogOut />}
            size="sm"
            borderRadius="full"
            bg="red.400"
            color="white"
            border="2px solid"
            borderColor="rgba(255, 255, 255, 0.3)"
            boxShadow="0 2px 8px rgba(255, 0, 0, 0.3)"
            opacity={0.85}
            onClick={handleLogout}
            _hover={{
              opacity: 1,
              bg: 'red.500',
              transform: 'scale(1.15)',
              boxShadow: '0 4px 12px rgba(255, 0, 0, 0.5)',
            }}
            transition="all 0.2s"
          />
        </Box>
      </Portal>

      {/* Login Modal */}
      <LoginWithSmsModal
        isOpen={isLoginOpen}
        onClose={onLoginClose}
        onLoginSuccess={() => {
          onLoginClose();
          window.location.reload();
        }}
      />
    </>
  );
};
